import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SettingsIcon,
} from 'lucide-react';
import * as React from 'react';
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';

import { cn } from '@/lib/utils';
import { Button, buttonVariants } from './button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import { useHolidays } from '@/features/holidays/HolidayProvider';
import { Link } from '@tanstack/react-router';

// Holiday types (minimal to avoid circular deps)
interface Holiday {
  id: string;
  calendar: { color: string; name: string };
  event: { title: string; description?: string; date: string };
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  showWeekNumber = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();
  const initialMonth = props.mode === 'single' ? props.selected : undefined;

  // Get holidays context
  const { getHolidaysForDate } = useHolidays();

  // Track current visible month
  const [currentMonth, setCurrentMonth] = React.useState<Date>(
    initialMonth || new Date(),
  );

  // Fetch holidays for the visible month
  const holidays = React.useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });

    return days.flatMap((day) => getHolidaysForDate(day));
  }, [currentMonth, getHolidaysForDate]);

  // Group holidays by date for efficient lookup
  const holidaysByDate = React.useMemo(() => {
    if (!holidays || holidays.length === 0) return {};

    const grouped: Record<string, Holiday[]> = {};
    holidays.forEach((holiday) => {
      const dateKey = holiday.event.date; // YYYY-MM-DD format

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(holiday);
    });

    return grouped;
  }, [holidays]);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      showWeekNumber={showWeekNumber}
      ISOWeek={true}
      defaultMonth={initialMonth}
      onMonthChange={setCurrentMonth}
      className={cn(
        `bg-background group/calendar p-3 [--cell-size:--spacing(8)]
        [[data-slot=card-content]_&]:bg-transparent
        [[data-slot=popover-content]_&]:bg-transparent`,
        'rtl:**:[.rdp-button\\_next>svg]:rotate-180',
        'rtl:**:[.rdp-button\\_previous>svg]:rotate-180',
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'relative flex flex-col gap-4 md:flex-row',
          defaultClassNames.months,
        ),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'has-focus:border-ring border-input has-focus:ring-ring/50 relative rounded-md border shadow-xs has-focus:ring-[3px]',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn('absolute inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'font-medium select-none',
          captionLayout === 'label'
            ? 'text-sm'
            : '[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-md pr-1 pl-2 text-sm [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        table: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-muted-foreground flex-1 rounded-md text-[0.8rem] font-normal select-none',
          defaultClassNames.weekday,
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn(
          'w-(--cell-size) select-none',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'text-muted-foreground text-[0.8rem] select-none',
          defaultClassNames.week_number,
        ),
        day: cn(
          'group/day relative aspect-square h-full w-full p-0 text-center select-none [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
          defaultClassNames.day,
        ),
        range_start: cn(
          'bg-accent rounded-l-md',
          defaultClassNames.range_start,
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('bg-accent rounded-r-md', defaultClassNames.range_end),
        today: cn(
          'bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: cn(
          'text-muted-foreground aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled,
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon className={cn('size-4', className)} {...props} />
            );
          }

          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={cn('size-4', className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn('size-4', className)} {...props} />
          );
        },
        DayButton: (dayButtonProps) => {
          const dateKey = format(dayButtonProps.day.date, 'yyyy-MM-dd');
          const dayHolidays = holidaysByDate[dateKey] || [];
          return (
            <CalendarDayButton {...dayButtonProps} holidays={dayHolidays} />
          );
        },
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  holidays,
  ...props
}: React.ComponentProps<typeof DayButton> & { holidays?: Holiday[] }) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  // Get holiday color (use first holiday's calendar color)
  const holidayColor =
    holidays && holidays.length > 0 ? holidays[0].calendar.color : undefined;

  // Build button with optional holiday background
  const button = (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        `data-[selected-single=true]:bg-primary
        data-[selected-single=true]:text-primary-foreground
        data-[range-middle=true]:bg-accent
        data-[range-middle=true]:text-accent-foreground
        data-[range-start=true]:bg-primary
        data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary
        data-[range-end=true]:text-primary-foreground
        group-data-[focused=true]/day:border-ring
        group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground
        flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1
        leading-none font-normal group-data-[focused=true]/day:relative
        group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px]
        data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md
        data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md
        data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-70`,
        defaultClassNames.day,
        // Holiday background styling
        holidayColor && 'relative',
        className,
      )}
      style={
        holidayColor
          ? ({ '--holiday-color': holidayColor } as React.CSSProperties)
          : undefined
      }
      {...props}
    >
      {/* Holiday background (circular, washed) */}
      {holidayColor && (
        <div
          className="pointer-events-none absolute inset-0 z-0 rounded-full opacity-15"
          style={{ backgroundColor: holidayColor }}
        />
      )}
      <span className="relative z-10">{props.children}</span>
    </Button>
  );

  // Wrap with tooltip if holidays exist
  if (holidays && holidays.length > 0) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-3">
              {/* Holiday list */}
              <div className="space-y-2">
                {holidays.map((holiday) => (
                  <div key={holiday.id} className="flex items-start gap-2">
                    <span
                      className="mt-1 size-2 shrink-0 rounded-full"
                      style={{ backgroundColor: holiday.calendar.color }}
                    />
                    <div>
                      <div className="font-medium">{holiday.event.title}</div>
                      <div className="text-primary-foreground/70 text-xs">
                        ({holiday.calendar.name})
                      </div>
                      {holiday.event.description && (
                        <div className="text-primary-foreground/70 mt-1 text-xs">
                          {holiday.event.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Settings link */}
              <div className="border-t pt-2">
                <Link
                  to="/settings/general"
                  className="text-primary-foreground/70 hover:text-primary-foreground flex items-center
                    gap-1.5 text-xs transition-colors"
                >
                  <SettingsIcon className="size-3" />
                  Manage holiday calendars
                </Link>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}

export { Calendar, CalendarDayButton };
